import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';
import {startOfDay, isAfter, format} from 'date-fns';

interface DateConfig {
  years?: number;
  months?: number;
  days?: number;
}

export const minDateValidator = (config: DateConfig = {}): ValidatorFn => (control: AbstractControl): { [key: string]: any } | null => {
  const inputDate = new Date(control.value);
  const currentDate = new Date();
  const minDate = new Date(currentDate.getFullYear() - (config.years || 0),
    currentDate.getMonth() - (config.months || 0),
    currentDate.getDate() - (config.days || 0));
  return inputDate < minDate ? {'minDate': {value: control.value}} : null;
};

export const maxDateValidator = (config: DateConfig = {}): ValidatorFn => (control: AbstractControl): { [key: string]: any } | null => {
  const inputDate = new Date(control.value);
  const currentDate = new Date();
  const maxDate = new Date(currentDate.getFullYear() + (config.years || 0),
    currentDate.getMonth() + (config.months || 0),
    currentDate.getDate() + (config.days || 0));
  return inputDate > maxDate ? {'maxDate': {value: control.value}} : null;
};

export const mustBeDifferentValidator = (currentValue: string): ValidatorFn => (control: AbstractControl) => {
  if(control.value === currentValue) {
    return {mustBeDifferent: true};
  }
  return null;
};

export const passwordStrengthValidator = (control: AbstractControl): ValidationErrors | null => {
  const value: string = control.value?.toString() || '';
  if(!value) return null;

  const hasUpper = /[A-Z]/.test(value);
  const hasLower = /[a-z]/.test(value);
  const hasDigit = /\d/.test(value);
  const hasSpecial = /[^A-Za-z0-9]/.test(value);
  const isLongEnough = value.length >= 9;

  const diversityCount = [hasUpper, hasLower, hasDigit, hasSpecial].filter(Boolean).length;

  const errors: ValidationErrors = {};

  if(!isLongEnough) errors.minlength = {requiredLength: 9, actualLength: value.length};
  if(diversityCount < 2) errors.pattern = {requiredPattern: 'password', actualValue: value};

  return Object.keys(errors).length ? errors : null;
};

export const dateMilliFromNowValidator = (config: {minDateMilliFromNow?: number, maxDateMilliFromNow?: number} = {}): ValidatorFn => (control: AbstractControl): ValidationErrors | null => {
  if(!control.value) return null;

  const errors: ValidationErrors = {};

  if(config.maxDateMilliFromNow !== undefined) {
    const maxDate = startOfDay(new Date(Date.now() + config.maxDateMilliFromNow));
    const inputDate = startOfDay(new Date(control.value));
    if(isAfter(inputDate, maxDate)) {
      errors.maxDateMilliFromNow = {maxDate: format(maxDate, 'MM-dd-yyyy')};
    }
  }

  if(config.minDateMilliFromNow !== undefined) {
    const minDate = startOfDay(new Date(Date.now() - config.minDateMilliFromNow));
    const inputDate = startOfDay(new Date(control.value));
    if(isAfter(minDate, inputDate)) {
      errors.minDateMilliFromNow = {minDate: format(minDate, 'MM-dd-yyyy')};
    }
  }

  return Object.keys(errors).length ? errors : null;
};

export const dateMilliToValidator = (config: {control: string, milliseconds: number}): ValidatorFn => (control: AbstractControl): ValidationErrors | null => {
  if(!control.value) return null;

  const controlValue = control.parent?.get(config.control)?.value;
  if(!controlValue) return null;

  const maxDate = startOfDay(new Date(new Date(controlValue).getTime() + config.milliseconds));
  const inputDate = startOfDay(new Date(control.value));

  if(isAfter(inputDate, maxDate)) {
    return {maxDateMilliTo: {maxDate: format(maxDate, 'MM-dd-yyyy'), control: config.control}};
  }

  return null;
};

